import { DEVELOPMENT, PRODUCTION } from "../application";
import v4 from "uuid/v4";

export const mapApplicationData = (responseData) => {
  if (!responseData || !responseData.payload || !responseData.payload.data) {
    return {};
  }

  const applications = responseData.payload.data;

  return applications.reduce((acc, app) => {
    const mappedApp = mapApplicationDatum(app);
    if (mappedApp && mappedApp.uuid) {
      acc[mappedApp.uuid] = mappedApp;
    }
    return acc;
  }, {});
};

export const mapApplicationDatum = (app) => {
  if (!app) return null;

  // Check appStatus.name for Production status (case-insensitive)
  const applicationStatus =
    app.appStatus?.name?.toUpperCase() === "PRODUCTION"
      ? PRODUCTION
      : DEVELOPMENT;

  // Map products to subscriptions format
  const subscriptions = app.products
    ? app.products.map((product) => ({
        uuid: product.id,
        title: product.productName,
        deprecated: product.deprecated || false,
        version: product.productVersion,
        apiEndpoints: product.apiEndpoints
          ? product.apiEndpoints.map((endpoint) => ({
              uuid: endpoint.id,
              title: endpoint.apiName,
              scope: endpoint.apiScope,
              scope_production: endpoint.apiScopeProduction,
            }))
          : [],
      }))
    : [];

  // Handle base64 image
  let imageObject = null;
  if (app.appImage) {
    // Check if appImage is a base64 string or has base64 data
    const imageData =
      typeof app.appImage === "string"
        ? app.appImage
        : app.appImage.imageFile || app.appImage;

    if (imageData) {
      let imageUrl;
      // If it doesn't start with 'data:', assume it's raw base64 and add the prefix
      if (
        typeof imageData === "string" &&
        !imageData.startsWith("http") &&
        !imageData.startsWith("data:")
      ) {
        // Assume PNG if no mime type specified, you can adjust based on your API
        imageUrl = `data:image/png;base64,${imageData}`;
      } else {
        imageUrl = imageData;
      }
      imageObject = {
        url: imageUrl,
        alt:
          app.appImage.altText || app.appImage.imageName || "Application Image",
      };
    }
  }

  return {
    nid: app.drupal_internal__nid || null, // May not exist in new API
    uuid: app.id,
    title: app.appName,
    created: app.created ? new Date(app.created) : new Date(),
    postLogoutRedirectUri: app.postlogoutRedirectUri
      ? app.postlogoutRedirectUri.split(",").map((uri) => uri.trim()) // Return plain strings, not objects
      : [],
    oauthRedirectUri: [
      ...(app.clientUriDev
        ? app.clientUriDev.split(",").map((uri) => uri.trim())
        : []),
      ...(app.clientUriProd
        ? app.clientUriProd.split(",").map((uri) => uri.trim())
        : []),
      ...(app.oauthRedirectUri
        ? app.oauthRedirectUri.split(",").map((uri) => uri.trim())
        : []),
    ],
    allowedGrantTypes: app.allowedGrantTypes
      ? app.allowedGrantTypes.map((grant) => grant.name)
      : [],
    allowedGrantTypesWithIds: app.allowedGrantTypes || [],
    description: {
      value: app.description || "",
      processed: app.description || "",
    },
    image: imageObject,
    subscriptions: subscriptions,
    subscriptionIds: subscriptions.map((sub) => sub.uuid),
    // Keep products array from the API response for new structure
    products: app.products || [],
    clientId: {
      development: app.clientIdDev || app.applicationIdDev,
      production: app.clientIdProd || app.applicationIdProd,
    },
    // Add clientIdDev and clientIdProd directly for backward compatibility
    clientIdDev: app.clientIdDev || app.applicationIdDev || null,
    clientIdProd: app.clientIdProd || app.applicationIdProd || null,
    applicationStatus,
    clientSecret:
      app.clientSecret || app.clientSecretDev || app.clientSecretProd || null,
    // Store additional fields from new API
    applicationCredentialsDev: app.applicationCredentialsDev,
    applicationCredentialsProd: app.applicationCredentialsProd,
    appStatus: app.appStatus,
    appTags: app.appTags || [],
    // Store organization information
    organizationName: app.organizationName || "",
    organizationId: app.organizationId,
  };
};

// Convert old format data to new API request format
export const mapToNewApiFormat = (oldData) => {
  return {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: v4(),
    },
    payload: {
      pageLimit: 50,
      pageOffset: 0,
      sortField: "created",
      sortOrder: "desc",
    },
  };
};
